import { useEffect, useMemo, useState } from 'react';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Button } from '@cloudflare/kumo/components/button';
import { Field } from '@cloudflare/kumo/components/field';
import { Input, Textarea } from '@cloudflare/kumo/components/input';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import {
  ArrowCounterClockwise,
  FloppyDisk,
  ArrowUUpLeft,
  DownloadSimple,
} from '@phosphor-icons/react';
import { api } from '@/lib/api';

// メールテンプレ管理画面。
// email_templates テーブルに row があれば EmailHelper が優先採用する仕組み。
// 行が無い場合は services/email.ts の hardcoded テンプレが使われる。
// admin はここで subject / HTML body / text body を {{var}} 置換つきで自由に編集できる。
// 利用可能な変数キーは右側の variables にドキュメントされる。
// memory: project_2026_approval_email_oneclick (メールテンプレは admin 編集可能)

interface TemplateVariableDoc {
  key: string;
  description: string;
}

interface TemplateListItem {
  templateKey: string;
  customized: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  variables: TemplateVariableDoc[];
}

interface TemplateDetail {
  templateKey: string;
  custom: {
    templateKey: string;
    subject: string;
    htmlBody: string;
    textBody: string;
    updatedAt: string;
    updatedBy: string | null;
  } | null;
  // hardcoded default を {{var}} トークン入りで描画したもの。「デフォルトを読み込む」の元データ。
  default: {
    subject: string;
    htmlBody: string;
    textBody: string;
  } | null;
  variables: TemplateVariableDoc[];
}

const TEMPLATE_LABEL: Record<string, string> = {
  'application-received': '申込受付 確認メール',
  'application-approved': '内定通知メール (出展 OK)',
  'payment-notification': '請求メール',
  'payment-reminder': '支払いリマインドメール',
  'payment-confirmed': '入金確認メール',
  'general-announcement': '一般お知らせメール',
};

export default function EmailTemplatesPage() {
  const [list, setList] = useState<TemplateListItem[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [draft, setDraft] = useState<{ subject: string; htmlBody: string; textBody: string }>({
    subject: '',
    htmlBody: '',
    textBody: '',
  });
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const dirty = useMemo(() => {
    if (!detail) return false;
    const baseline = detail.custom ?? { subject: '', htmlBody: '', textBody: '' };
    return (
      draft.subject !== baseline.subject ||
      draft.htmlBody !== baseline.htmlBody ||
      draft.textBody !== baseline.textBody
    );
  }, [detail, draft]);

  const fetchList = async () => {
    setLoadingList(true);
    setError(null);
    try {
      const res = await api.get<{ data: TemplateListItem[] }>('/api/admin/email-templates');
      const data = res.data.data ?? [];
      setList(data);
      if (!selectedKey && data.length > 0) {
        setSelectedKey(data[0].templateKey);
      }
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoadingList(false);
    }
  };

  const fetchDetail = async (key: string) => {
    setLoadingDetail(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.get<{ data: TemplateDetail }>(`/api/admin/email-templates/${key}`);
      const d = res.data.data ?? (res.data as unknown as TemplateDetail);
      setDetail(d);
      setDraft({
        subject: d.custom?.subject ?? '',
        htmlBody: d.custom?.htmlBody ?? '',
        textBody: d.custom?.textBody ?? '',
      });
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedKey) fetchDetail(selectedKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const save = async () => {
    if (!selectedKey) return;
    if (!draft.subject || !draft.htmlBody || !draft.textBody) {
      setError('subject / HTML / Text すべて入力してください');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await api.put(`/api/admin/email-templates/${selectedKey}`, draft);
      setInfo('保存しました。次回送信から反映されます。');
      await Promise.all([fetchList(), fetchDetail(selectedKey)]);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  // hardcoded default ({{var}} 入り) を編集ボックスに流し込む。これをベースに編集して保存できる。
  const loadDefault = () => {
    if (!detail?.default) return;
    if (dirty && !confirm('編集中の内容をデフォルトで上書きします。よろしいですか?')) return;
    setDraft({
      subject: detail.default.subject,
      htmlBody: detail.default.htmlBody,
      textBody: detail.default.textBody,
    });
    setInfo('デフォルト内容を読み込みました。編集して保存してください。');
  };

  const revert = async () => {
    if (!selectedKey) return;
    if (!confirm('カスタマイズを削除し hardcoded default に戻します。よろしいですか?')) return;
    setReverting(true);
    setError(null);
    setInfo(null);
    try {
      await api.delete(`/api/admin/email-templates/${selectedKey}`);
      setInfo('カスタマイズを削除しました。default に戻りました。');
      await Promise.all([fetchList(), fetchDetail(selectedKey)]);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setReverting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-kumo-default">メールテンプレ</h1>
          <p className="text-sm text-kumo-muted">
            DB に row がある場合のみ admin の編集内容が使われ、無ければ hardcoded default が使われます。
            <code className="mx-1 rounded bg-kumo-base px-1">{'{{var}}'}</code>
            形式の placeholder が送信時に置換されます。
          </p>
        </div>
        <Button
          variant="ghost"
          icon={ArrowCounterClockwise}
          loading={loadingList}
          onClick={fetchList}
        >
          再読込
        </Button>
      </div>

      {error && <Banner variant="error">{error}</Banner>}
      {info && <Banner variant="default">{info}</Banner>}

      <div className="grid grid-cols-[260px_minmax(0,1fr)] gap-4">
        <Surface className="p-2">
          {loadingList ? (
            <div className="flex items-center justify-center p-4">
              <Loader />
            </div>
          ) : (
            <ul className="flex flex-col gap-1">
              {list.map((item) => {
                const active = item.templateKey === selectedKey;
                return (
                  <li key={item.templateKey}>
                    <button
                      onClick={() => setSelectedKey(item.templateKey)}
                      className={`flex w-full flex-col gap-1 rounded px-3 py-2 text-left text-sm ${
                        active
                          ? 'bg-kumo-accent text-kumo-default'
                          : 'hover:bg-kumo-base text-kumo-default'
                      }`}
                    >
                      <span className="font-medium">
                        {TEMPLATE_LABEL[item.templateKey] ?? item.templateKey}
                      </span>
                      <span className="text-xs text-kumo-muted">{item.templateKey}</span>
                      {item.customized && (
                        <Badge variant="primary" className="self-start">
                          customized
                        </Badge>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Surface>

        <Surface className="p-4">
          {!selectedKey || loadingDetail || !detail ? (
            <div className="flex items-center justify-center p-8">
              <Loader />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <h2 className="text-base font-semibold text-kumo-default">
                  {TEMPLATE_LABEL[detail.templateKey] ?? detail.templateKey}
                </h2>
                {detail.custom ? (
                  <p className="mt-1 text-xs text-kumo-muted">
                    最終更新: {new Date(detail.custom.updatedAt).toLocaleString('ja-JP')}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-kumo-muted">
                    まだカスタマイズされていません (hardcoded default を使用中)。「デフォルトを読み込む」でひな形を編集できます。
                  </p>
                )}
              </div>

              <Field label="件名">
                <Input
                  placeholder="NT東京2026 - 出展承認のお知らせ"
                  value={draft.subject}
                  onChange={(e) => setDraft((d) => ({ ...d, subject: e.currentTarget.value }))}
                />
              </Field>

              <Field label="HTML 本文">
                <Textarea
                  placeholder="<!DOCTYPE html>..."
                  value={draft.htmlBody}
                  onChange={(e) => setDraft((d) => ({ ...d, htmlBody: e.currentTarget.value }))}
                  rows={16}
                  className="font-mono text-xs"
                />
              </Field>

              <Field label="プレーンテキスト本文 (HTML 非対応クライアント向け)">
                <Textarea
                  placeholder="プレーンテキスト版..."
                  value={draft.textBody}
                  onChange={(e) => setDraft((d) => ({ ...d, textBody: e.currentTarget.value }))}
                  rows={10}
                  className="font-mono text-xs"
                />
              </Field>

              <Surface className="bg-kumo-base p-3">
                <div className="text-xs font-medium text-kumo-default">
                  利用可能な placeholder
                </div>
                <ul className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-kumo-muted">
                  {detail.variables.map((v) => (
                    <li key={v.key}>
                      <code className="rounded bg-white/40 px-1">{`{{${v.key}}}`}</code> —{' '}
                      {v.description}
                    </li>
                  ))}
                </ul>
              </Surface>

              <div className="flex items-center justify-between gap-2">
                <Button
                  variant="secondary"
                  icon={DownloadSimple}
                  disabled={!detail.default}
                  onClick={loadDefault}
                >
                  デフォルトを読み込む
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    icon={ArrowUUpLeft}
                    loading={reverting}
                    disabled={!detail.custom}
                    onClick={revert}
                  >
                    default に戻す
                  </Button>
                  <Button
                    variant="primary"
                    icon={FloppyDisk}
                    loading={saving}
                    disabled={!dirty}
                    onClick={save}
                  >
                    保存
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Surface>
      </div>
    </div>
  );
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string; message?: string } } }).response;
    return r?.data?.error ?? r?.data?.message ?? '読み込みに失敗しました';
  }
  return e instanceof Error ? e.message : '読み込みに失敗しました';
}
